import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { ModelingByList } from '@enterprise/api';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';
import type { PercentCellProps } from '@components/grid/renderers/types/percent-cell-renderer.params';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import PercentCellRenderer from '@components/grid/renderers/percent-cell.renderer';

export const useGetModelingByListColumns = () => {
  return useMemo(() => {
    return [
      {
        children: [
          {
            cellDataType: 'number',
            field: 'mailingId',
            headerClass: ['bg-blue-900'],
            headerName: 'ID',
            hide: true,
            width: 125,
          },
          {
            cellDataType: 'date',
            cellRenderer: DateCellRenderer,
            field: 'mailDate',
            headerClass: ['bg-blue-900'],
            headerName: 'Mail Date',
            width: 175,
          },
          {
            field: 'mailCode',
            headerClass: ['bg-blue-900'],
            headerName: 'Mail Code',
            type: ['textColumn'],
            width: 150,
          },
          {
            cellRenderer: ListSegmentRenderer,
            cellRendererParams: (params: ListSegmentCellProps<ModelingByList>) => {
              return {
                ...params,
                header: 'listDisplayName',
                subHeader: 'listManagerName',
              } as ListSegmentCellProps<ModelingByList>;
            },
            headerClass: ['bg-blue-900'],
            headerName: 'List/Segment',
            wrapText: true,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing Information',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-gray-200', 'text-gray-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityPosted',
            headerClass: ['bg-gray-800'],
            headerName: 'Posted',
            type: ['textColumn'],
            width: 200,
          },
        ],
        headerClass: ['bg-gray-900'],
        headerName: '',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-teal-50', 'text-teal-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityTwentile1To4',
            headerClass: ['bg-teal-800'],
            headerName: '1-4 Count',
            type: ['textColumn'],
            width: 200,
          },
          {
            cellClass: ['bg-teal-100', 'text-teal-900'],
            cellRenderer: PercentCellRenderer,
            cellRendererParams: (params: PercentCellProps<ModelingByList, number>) => {
              return {
                api: params.api,
                grandTotalFields: ['quantityPosted'],
                node: params.node,
                totalFields: ['quantityTwentile1To4'],
                value: params.value,
              };
            },
            field: 'percentTwentile1To4',
            headerClass: ['bg-teal-800'],
            headerName: '1-4 %',
            type: ['textColumn'],
            width: 175,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-teal-50', 'text-teal-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityTwentile5To20',
            headerClass: ['bg-teal-800'],
            headerName: '5-20 Count',
            type: ['textColumn'],
            width: 175,
          },
          {
            cellClass: ['bg-teal-100', 'text-teal-900'],
            cellRenderer: PercentCellRenderer,
            cellRendererParams: (params: PercentCellProps<ModelingByList, number>) => {
              return {
                api: params.api,
                grandTotalFields: ['quantityPosted'],
                node: params.node,
                totalFields: ['quantityTwentile5To20'],
                value: params.value,
              };
            },
            field: 'percentTwentile5To20',
            headerClass: ['bg-teal-800'],
            headerName: '5-20 %',
            type: ['textColumn'],
            width: 175,
          },
        ],
        headerClass: ['bg-teal-900'],
        headerGroupComponentParams: {
          icon: 'fa-ranking-star',
        },
        headerName: 'Modeling Overview',
      },
    ] as ColGroupDef<ModelingByList>[];
  }, []);
};
